import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CallWindow,
  type Event,
  HelcimClient,
  type JobType,
  type JobTypeSubtype,
  type Property,
  SetPreferredHelcimCardReq,
  type TimesheetDepartment,
  type User,
} from '@kalos/kalos-rpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form as FormContext,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  TimePicker,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format, getDay, isValid, parseISO } from 'date-fns';
import { CardOnFileChip, CardOnFileModal } from 'kalos-modules';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type SubmitErrorHandler, type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  BILLING_TERMS_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  ENDPOINT,
  EVENT_STATUS_LIST,
  JOB_STATUS_COLORS,
  JOB_STATUS_LIST,
  MAINTENANCE_JOB_TYPE_ID,
  paymentTypeOptionsFor,
  RESIDENTIAL_OPTIONS,
} from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { useFeatureFlag } from '../../../../featureFlags/useFeatureFlag';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { useDailyJobTypeCaps } from '../../../../hooks/react-query/useCallWindowJobCapQuery';
import { useWindowLimitDateOverridesForDate } from '../../../../hooks/react-query/useCallWindowLimitDateQuery';
import { useWindowLimitOverrides } from '../../../../hooks/react-query/useCallWindowLimitQuery';
import { useContactPreferenceListQuery } from '../../../../hooks/react-query/useContactPreference';
import { useCustomerPrioritiesQuery } from '../../../../hooks/react-query/useCustomerPrioritiesQuery';
import { useDispatchableTechsQuery } from '../../../../hooks/react-query/useDispatchQuery';
import { useEventBatchGetQuery } from '../../../../hooks/react-query/useEventsQuery';
import { useJobSubTypesQuery } from '../../../../hooks/react-query/useJobSubTypesQuery';
import { useJobTypesQuery } from '../../../../hooks/react-query/useJobTypesQuery';
import { usePropertyContactsQuery } from '../../../../hooks/react-query/usePropertyContactsQuery';
import { useReferralSourceListQuery } from '../../../../hooks/react-query/useReferralSource';
import { useResolveEventTradeQuery } from '../../../../hooks/react-query/useResolveEventTradeQuery';
import { useSubtypeTradesQuery } from '../../../../hooks/react-query/useSubtypeTradesQuery';
import {
  getBlockedJobTypesForWindow,
  getBlocksForDate,
} from '../../../../hooks/react-query/useWindowBlockQuery';
import { useCallbackJobOptions } from '../../../../hooks/useCallbackJobOptions';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { resolveWindowLimits } from '../../../../tools/callWindowLimits';
import { addResponseTime, customerPriorityHint } from '../../../../tools/customerPriority';
import { formatTimeFull } from '../../../../tools/formatTimeUtils';
import { type DirtyModelFields } from '../../../../tools/helpers';
import {
  formatSlaDueHint,
  normalizeServiceChannelWo,
  parseSlaDue,
} from '../../../../tools/serviceChannel';
import { normalizeTime } from '../../../../tools/timeUtils';
import {
  type BookingTrade,
  buildSubtypeOptions,
  groupSubtypeOptions,
  isSubtypeRequired,
  MEP_TRADE_IDS,
  REFRIGERATION_SUBTYPE_ID,
  RESIDENTIAL_MEP_DEPARTMENT_ID,
  SUBTYPE_REQUIRED_MESSAGE,
  TRADE_CATCH_ALL_SUBTYPE_ID,
} from '../../../../utils/eventTrade';
import { normalizePhoneForSearch } from '../../../CallWindowCalendar/utils/customerHelpers';
import { type DateStatusBreakout } from '../../CallWindow/utils';
import { SUBTYPE_SELECT_VIEWPORT_CLASS, SubtypeSelectItems } from '../../SubtypeSelectItems';
import { TradeChip } from '../../TradeChip';
import { useSchedulerStyle } from '../context/schedulerStyle';
import { type CustomerLevelData } from '../hooks/useEventSubmit';
import { useOutsideServiceArea } from '../hooks/useOutsideServiceArea';
import { useRecentCompletedVisit } from '../hooks/useRecentCompletedVisit';
import { anchorFirstInvalidField } from '../utils/anchorFirstInvalidField';
import { CallbackPrompt } from './CallbackPrompt';
import { CallWindowAvailability } from './CallWindowAvailability';
import { CustomerJobsAccordion } from './CustomerJobsAccordion';
import { CustomerPropertyContractGrid } from './CustomerPropertyContractGrid';
import { OutsideServiceAreaBanner } from './OutsideServiceAreaBanner';
import { TechnicianAssignment } from './TechnicianAssignment';

/** Format date (and time only if not midnight) as human readable; avoids showing "12:00am" / "00:00". */
const formatDateOptionalTime = (dateStr: string, timeStr: string | undefined): string => {
  const normalized = timeStr ? normalizeTime(timeStr) : '00:00';
  const dateFormatted = format(parseISO(dateStr), 'EEEE, MMM d, yyyy');
  if (normalized === '00:00') return dateFormatted;
  return `${dateFormatted} ${formatTimeFull(normalized)}`;
};

// -------------------- Types & Schema --------------------
export interface ServiceCallDetailsProps {
  date: string; // ISO yyyy-MM-dd
  callWindows: CallWindow[];
  customer: User;
  property: Property;
  onBack?: () => void;
  onChangeCustomer?: () => void;
  onChangeProperty?: () => void;
  onSubmit: (
    event: Partial<Event>,
    dirtyFields?: DirtyModelFields<DetailsFormValues>,
    customerLevelData?: CustomerLevelData,
    internalNotes?: string,
  ) => void;
  onCancel?: () => void;
  hideCancelButton?: boolean;
  /** Pre-selected timeWindow to use */
  selectedTimeWindow?: CallWindow | null;
  /** Callback when user selects a time window */
  onTimeWindowChange?: (windowId: number) => void;
  /** Callback when date changes */
  onDateChange?: (date: string) => void;
  defaultDepartmentId?: number;
  /** The scheduler's trade; its catch-all Sub Type is pre-selected. */
  defaultTradeId?: number;
  /** The trade this booking resolves to (see JobCreationFlow). */
  bookingTrade?: BookingTrade;
  defaultStatus?: string;
  departments?: TimesheetDepartment[];
  technicians?: User[];
  loading?: boolean;
  onJobCreated?: () => void;
  windowEventCounts?: Record<number, Record<number, number>>;
  windowTechCounts?: Record<number, Record<number, number>>;
  windowJobTypeBreakdown?: Record<number, Record<number, Record<string, number>>>;
  windowMaintConfirmedCounts?: Record<number, Record<number, number>>;
  /** Confirmed job counts for capacityDate: dept -> job type name -> count. */
  dayJobTypeConfirmedCounts?: Record<number, Record<string, number>>;
  /**
   * The date the save will actually land on. Equals `date` everywhere except the job-info edit
   * flow, where the saved dateStarted comes from formState. Daily-cap checks must use this.
   */
  capacityDate?: string;
  /** Daily category pill breakout for the selected date (matches the week-view header). */
  dateBreakout?: DateStatusBreakout;
  existingEvent?: Event;
  isCustomTime?: boolean;
  /** Form state to initialize and maintain across date changes */
  formState?: Partial<Event>;
  /** Callback to update form state when form fields change */
  onFormDataChange?: (
    data: Partial<Event>,
    dirtyFields?: DirtyModelFields<DetailsFormValues> & {
      dateStarted?: boolean;
      dateEnded?: boolean;
    },
  ) => void;
  /** Whether capacity data is still loading (prevents premature recommendations) */
  isCapacityLoading?: boolean;
  /** Hide call window selection and enforce custom time only (e.g. job info edit context) */
  hideCallWindowSelection?: boolean;
  /** Window block events for the current date range */
  windowBlocks?: Event[];

  /** Parent's dirty fields (from draft) - when editing, used to disable Update button when no changes */
  preservedDirtyFields?: DirtyModelFields<DetailsFormValues> & {
    dateStarted?: boolean;
    dateEnded?: boolean;
    timeWindowId?: boolean;
  };
  /** Whether the recent-visit callback prompt has been dismissed (shared across steps) */
  callbackPromptDismissed?: boolean;
  /** Dismiss the recent-visit callback prompt (shared with Step 2) */
  onDismissCallbackPrompt?: () => void;
}

/** Department a new job starts on when the caller passes none; JobCreationFlow falls back to the same one. */
export const DEFAULT_DEPARTMENT_ID = 20;
const DEFAULT_STATUS = 'Confirmed';

// Special value indicating custom time is being used instead of a preset window
const CUSTOM_TIME_WINDOW_ID = -1;

// Sector has no default — 0 (Commercial) and 1 (Residential) are both real values, so "not yet
// chosen" needs an out-of-range sentinel rather than a falsy one.
const UNSET_SECTOR = -1;

// Tailwind's default aria variants omit `invalid`, so the arbitrary form is required here.
const INVALID_FIELD_CLASSES = 'aria-[invalid=true]:border-red-500';

const detailsSchema = z
  .object({
    departmentId: z.coerce.number().min(1, 'Department is required'),
    logJobStatus: z.string().min(1, 'Status is required'),
    timeWindowId: z.coerce.number().refine((val) => val >= 1 || val === CUSTOM_TIME_WINDOW_ID, {
      message: 'Time window is required',
    }),
    name: z.string().optional(),
    description: z.string().min(1, 'Issue description is required'),
    logNotes: z.string().optional(),
    assignTechnicians: z.boolean().optional(),
    technicianIds: z.array(z.number()).optional(),
    jobTypeId: z.coerce.number().min(1, 'Job Type is required'),
    jobSubtypeId: z.coerce.number().optional(),
    logPaymentType: z.string().min(1, 'Payment Type is required'),
    amountQuoted: z.string().min(1, 'Amount quoted is required'),
    isCallback: z.coerce.number().optional(),
    callbackOriginalId: z.coerce.number().optional(),
    highPriority: z.coerce.number().optional(),
    isLmpc: z.coerce.number().optional(),
    isResidential: z.coerce.number(),
    contactMethod: z.string().min(1, 'Contact Method is required'),
    referralSourceId: z.string().optional(),
    contactPreferenceId: z.string().optional(),
    referralSourceOther: z.string().optional(),
    enRouteCallNumber: z.string().optional(),
    isNewCustomer: z.coerce.number().optional(),
    paymentAgreement: z.string().min(1, 'Payment Agreement is required'),
    internalNotes: z.string().optional(),
    serviceChannelWo: z.string().optional(),
    slaDue: z.string().optional(),
    customerPriorityId: z.coerce.number().optional(),
  })
  .refine(
    (data) => {
      // If isCallback is checked (1), callbackOriginalId must be selected
      if (data.isCallback === 1 && (!data.callbackOriginalId || data.callbackOriginalId === 0)) {
        return false;
      }
      return true;
    },
    {
      message: "Callback job must be selected when 'Is Callback' is checked",
      path: ['callbackOriginalId'],
    },
  )
  .refine((data) => data.isResidential === 0 || data.isResidential === 1, {
    // Checked here rather than on the field so the inferred type stays `number` — the field really
    // does hold UNSET_SECTOR until the user picks one.
    message: 'Sector is required',
    path: ['isResidential'],
  });
export type DetailsFormValues = z.infer<typeof detailsSchema>;

const withSubtypeRule = (requiresSubtype?: boolean) =>
  detailsSchema.superRefine((data, ctx) => {
    if (isSubtypeRequired(data.departmentId, requiresSubtype) && !data.jobSubtypeId) {
      ctx.addIssue({ code: 'custom', message: SUBTYPE_REQUIRED_MESSAGE, path: ['jobSubtypeId'] });
    }
  });

// -------------------- Component --------------------
export const ServiceCallDetails = ({
  date,
  callWindows,
  customer,
  property,
  onBack,
  onChangeCustomer,
  onSubmit,
  selectedTimeWindow,
  onTimeWindowChange,
  onDateChange,
  defaultDepartmentId = DEFAULT_DEPARTMENT_ID,
  defaultTradeId,
  bookingTrade,
  defaultStatus = DEFAULT_STATUS,
  onCancel,
  hideCancelButton = false,
  departments: departmentProp,
  technicians: _techniciansProp,
  onChangeProperty,
  windowEventCounts = {},
  windowTechCounts = {},
  windowJobTypeBreakdown = {},
  windowMaintConfirmedCounts = {},
  dayJobTypeConfirmedCounts = {},
  capacityDate,
  dateBreakout,
  existingEvent,
  loading = false,
  formState = {},
  onFormDataChange,
  isCapacityLoading = false,
  isCustomTime = false,
  hideCallWindowSelection = false,
  preservedDirtyFields,
  windowBlocks,
  callbackPromptDismissed = false,
  onDismissCallbackPrompt,
}: ServiceCallDetailsProps) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const isMobileDevice = useMobileDevice();
  const [cardOnFileModalOpen, setCardOnFileModalOpen] = useState(false);
  const isTenantOccupied = property?.propertyOwnershipTypeId === 2;
  const showCardOnFile = !!customer?.id && !!ENDPOINT;

  const invalidateCardsOnFile = () => {
    if (customer?.id) {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.helcimCardsOnFile.root, customer.id],
      });
    }
  };

  const isRecentCustomer = customer?.dateCreated
    ? differenceInDays(new Date(), parseISO(customer.dateCreated)) <= 30
    : false;

  // Use departments provided by parent component
  const departmentOptions: TimesheetDepartment[] = departmentProp ?? [];

  const { data: customerPrioritiesData } = useCustomerPrioritiesQuery({
    userId: customer?.id ?? 0,
  });
  const customerPriorities = customerPrioritiesData?.results ?? [];

  const jobStartForSla = (): Date => {
    if (hideCallWindowSelection && formState?.dateStarted) {
      const d = parseISO(formState.dateStarted.replace(' ', 'T'));
      if (isValid(d)) return d;
    }
    const timeStr = selectedTimeWindow?.startTime
      ? normalizeTime(selectedTimeWindow.startTime)
      : '00:00';
    const d = parseISO(`${date}T${timeStr}`);
    return isValid(d) ? d : new Date();
  };

  // Fetch job types and subtypes
  const { data: jobTypesData } = useJobTypesQuery();
  const totalJobTypeCount = jobTypesData?.length ?? 0;
  const { data: jobSubtypesData } = useJobSubTypesQuery();
  const [jobTypeSubtypes, setJobTypeSubtypes] = useState<JobTypeSubtype[]>([]);

  // Fetch dispatchable technicians with status
  const { data: techsData } = useDispatchableTechsQuery({
    filter: {},
    enabled: true,
  });

  // Use dispatchable techs if available, otherwise fall back to provided technicians
  const techniciansWithStatus = useMemo<any[]>(() => {
    if (techsData?.results) {
      return techsData.results as any[];
    }
    return (_techniciansProp ?? []) as any[];
  }, [techsData, _techniciansProp]);

  // Feeds the recent-visit callback prompt, which only shows on new jobs
  const propertyEventsQuery = useEventBatchGetQuery({
    filters: {
      propertyId: property?.id || 0,
      isActive: 1,
      withoutLimit: true,
      orderBy: 'id',
      orderDir: 'DESC',
    },
    enabled: !!property?.id && !existingEvent,
  });

  // Fetch job type subtypes when component mounts
  useEffect(() => {
    const fetchJobTypeSubtypes = async () => {
      try {
        const { JobTypeSubtypeClientService } = await import('../../../../tools/helpers');
        const subtypes = await JobTypeSubtypeClientService.loadJobTypeSubtypes();
        setJobTypeSubtypes(subtypes || []);
      } catch (error) {
        console.error('Failed to fetch job type subtypes:', error);
        setJobTypeSubtypes([]);
      }
    };

    fetchJobTypeSubtypes();
  }, []);

  // Use JavaScript day index directly (0 = Sunday ... 6 = Saturday)
  const dayOfWeek = useMemo(() => getDay(parseISO(date)), [date]);
  const dayWindows = useMemo(
    () => callWindows.filter((w) => Number(w.dayOfWeek) === dayOfWeek),
    [callWindows, dayOfWeek],
  );
  const sortedWindows = useMemo(
    () => [...dayWindows].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [dayWindows],
  );

  const jobStatusOptions = useMemo(() => {
    const base = existingEvent ? EVENT_STATUS_LIST : JOB_STATUS_LIST;
    const current = existingEvent?.logJobStatus?.trim();
    if (current && !base.includes(current)) {
      return [...base, current];
    }
    return base;
  }, [existingEvent]);

  // Use the selected time window ID directly - capacity will be checked when needed
  const effectiveInitialWindowId = selectedTimeWindow?.id ?? 0;

  const [showCustomerJobsDialog, setShowCustomerJobsDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<{
    eventPartial: Partial<Event>;
    dirtyFields: any;
    customerData: CustomerLevelData;
    internalNotes?: string;
  } | null>(null);

  const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
    <FormLabel showError>
      {children} <span className="text-red-500">*</span>
    </FormLabel>
  );

  const OptionalLabel = ({ children }: { children: React.ReactNode }) => (
    <FormLabel>
      {children} <span className="text-muted-foreground text-xs font-normal">(optional)</span>
    </FormLabel>
  );

  const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <h3 className="border-b border-gray-200 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
      {children}
    </h3>
  );

  // Read at validation time so the resolve answer applies without rebuilding the form
  const subtypeRuleRef = useRef<{ requiresSubtype?: boolean; existingEvent?: Event }>({
    existingEvent,
  });
  const form = useForm<DetailsFormValues>({
    resolver: (values, context, options) => {
      // Create, or an edit that changes Department or Sub Type
      const { requiresSubtype, existingEvent: saved } = subtypeRuleRef.current;
      const enforce =
        !saved ||
        values.departmentId !== saved.departmentId ||
        (values.jobSubtypeId ?? 0) !== saved.jobSubtypeId;
      return zodResolver(enforce ? withSubtypeRule(requiresSubtype) : detailsSchema)(
        values,
        context,
        options,
      );
    },
    mode: 'onChange',
    // Anchoring picks the target in visual order; RHF's own focus uses registration order.
    shouldFocusError: false,
    defaultValues: {
      // Map existing event data for rescheduling scenarios, prioritizing formState over existingEvent for new jobs
      departmentId: formState.departmentId ?? existingEvent?.departmentId ?? defaultDepartmentId,
      logJobStatus: formState.logJobStatus ?? existingEvent?.logJobStatus ?? defaultStatus,
      timeWindowId: hideCallWindowSelection ? CUSTOM_TIME_WINDOW_ID : selectedTimeWindow?.id ?? 0,
      name:
        formState.name ??
        existingEvent?.name ??
        (property?.address
          ? `${property.address}${property.city ? ' ' + property.city : ''}${property.state ? ', ' + property.state : ''} ${property.zip ?? ''}`.trim()
          : ''),
      description: formState.description ?? existingEvent?.description ?? '',
      logNotes: formState.logNotes ?? existingEvent?.logNotes ?? '',
      assignTechnicians:
        formState.logTechnicianAssigned || existingEvent?.logTechnicianAssigned ? true : false,
      technicianIds:
        formState.logTechnicianAssigned || existingEvent?.logTechnicianAssigned
          ? (formState.logTechnicianAssigned || existingEvent?.logTechnicianAssigned || '')
              .split(',')
              .map((id) => Number(id))
              .filter((id) => !isNaN(id))
          : [],
      // New fields
      jobTypeId: formState.jobTypeId ?? existingEvent?.jobTypeId ?? 0,
      jobSubtypeId: formState.jobSubtypeId ?? existingEvent?.jobSubtypeId ?? 0,
      logPaymentType: formState.logPaymentType ?? existingEvent?.logPaymentType ?? 'Credit Card',
      amountQuoted:
        formState.amountQuoted?.toString() ?? existingEvent?.amountQuoted?.toString() ?? '',
      isCallback: formState.isCallback ?? existingEvent?.isCallback ?? 0,
      callbackOriginalId: formState.callbackOriginalId ?? existingEvent?.callbackOriginalId ?? 0,
      highPriority: formState.highPriority ?? existingEvent?.highPriority ?? 0,
      isLmpc: formState.isLmpc ?? existingEvent?.isLmpc ?? 0,
      isResidential: formState.isResidential ?? existingEvent?.isResidential ?? UNSET_SECTOR,
      contactMethod: customer?.preferredContact || 'Phone Call',
      referralSourceId: customer?.referralSourceId ? String(customer.referralSourceId) : '',
      contactPreferenceId: customer?.contactPreferenceId
        ? String(customer.contactPreferenceId)
        : '',
      referralSourceOther: customer?.referralSourceOther || '',
      enRouteCallNumber: customer?.enRouteCall || '',
      isNewCustomer:
        formState.isNewCustomer ?? existingEvent?.isNewCustomer ?? (isRecentCustomer ? 1 : 0),
      paymentAgreement: customer?.billingTerms || '',
      internalNotes: '',
      serviceChannelWo: formState.serviceChannelWo ?? existingEvent?.serviceChannelWo ?? '',
      slaDue: formState.slaDue ?? existingEvent?.slaDue ?? '',
      customerPriorityId: formState.customerPriorityId ?? existingEvent?.customerPriorityId ?? 0,
    },
  });

  // Watch the form fields for UI logic
  const currentDepartmentId = form.watch('departmentId');
  const currentJobTypeId = form.watch('jobTypeId');
  const currentIsResidential = form.watch('isResidential');
  const currentIsCallback = form.watch('isCallback');
  const currentJobSubtypeId = form.watch('jobSubtypeId') ?? 0;
  const tradeQuery = useResolveEventTradeQuery({
    departmentId: currentDepartmentId,
    jobTypeId: currentJobTypeId,
    jobSubtypeId: currentJobSubtypeId,
    isResidential: currentIsResidential,
  });
  const tradeResolution = tradeQuery.data;
  // The kept answer belongs to the fields before the last change; only the TRADE tag shows it
  const requiresSubtype = tradeQuery.isPlaceholderData
    ? undefined
    : tradeResolution?.requiresSubtype;
  const subtypeRequired = isSubtypeRequired(currentDepartmentId, requiresSubtype);
  // Layout effect: Radix Select's hidden select fires Sub Type's onChange from a child effect
  useLayoutEffect(() => {
    subtypeRuleRef.current = { requiresSubtype, existingEvent };
  }, [requiresSubtype, existingEvent]);
  // Re-check Sub Type once the department's requirement changes
  useEffect(() => {
    if (form.formState.isSubmitted || form.getFieldState('jobSubtypeId').invalid)
      void form.trigger('jobSubtypeId');
  }, [subtypeRequired, form]);
  const currentJobStatus = form.watch('logJobStatus');

  // Overrides of department-owned windows' limits, keyed by callWindowId. Shares its fetch
  // with the capacity banner in JobCreationFlow.
  const windowLimitOverrides = useWindowLimitOverrides({ enabled: !!currentDepartmentId });

  // Daily job-type caps for this department: dayOfWeek -> jobTypeId -> maxPerDay. Unlike the
  // per-window maint_max, a cap is a ceiling for the whole day across every window.
  const dailyJobTypeCaps = useDailyJobTypeCaps({
    departmentId: currentDepartmentId,
    eventTradeId: bookingTrade?.tradeId,
  });

  // The cap that applies to what is being booked, plus how much of it is already used.
  // Null when nothing constrains this booking, so callers can treat null as "no cap".
  //
  // maxPerDay <= 0 is read as "no cap configured" rather than "none allowed", matching the
  // week-view counter (CallWindowColumn.tsx:137-143). Rows can hold 0 from before the stepper
  // floored at 1, and treating those as a total block would silently freeze booking.
  // effectiveCapacityDate is the day the save lands on, which is not `date` in the job-info edit
  // flow -- the cap and the counts must both be read against it or they describe a different day
  // than the one being written to.
  const effectiveCapacityDate = capacityDate ?? date;
  // Date overrides for the day the save lands on. That day only differs from `date` in the
  // job-info edit flow, where the window picker is hidden, so the same map serves the
  // availability panel below.
  const windowLimitDateOverrides = useWindowLimitDateOverridesForDate({
    date: effectiveCapacityDate,
    enabled: !!currentDepartmentId,
  });

  const dailyCapState = useMemo(() => {
    if (!currentDepartmentId || !currentJobTypeId) return null;
    const parsed = parseISO(effectiveCapacityDate);
    if (!isValid(parsed)) return null;

    const max = dailyJobTypeCaps[getDay(parsed)]?.[currentJobTypeId];
    if (max === undefined || max <= 0) return null;

    const jobTypeName = jobTypesData?.find((jt: JobType) => jt.id === currentJobTypeId)?.name;
    if (!jobTypeName) return null;

    const used = dayJobTypeConfirmedCounts[currentDepartmentId]?.[jobTypeName] ?? 0;
    return { max, used, jobTypeName, reached: used >= max };
  }, [
    currentDepartmentId,
    currentJobTypeId,
    effectiveCapacityDate,
    dailyJobTypeCaps,
    jobTypesData,
    dayJobTypeConfirmedCounts,
  ]);

  // A cap counts CONFIRMED jobs of one type on one day, so a save only consumes cap when it adds
  // to those three coordinates. An edit that leaves all three unchanged consumes nothing, and
  // blocking it would strand the row: a Pending job on a full day, or any job on an over-cap day
  // (where the self-subtraction still leaves used - 1 >= max), could never be saved again.
  // Over-cap days are reachable -- enforcement is frontend-only and existing data is unbounded.
  const capConsumingSave = useMemo(() => {
    if (!existingEvent) return true;
    return (
      effectiveCapacityDate !== existingEvent.dateStarted.split(' ')[0] ||
      currentJobTypeId !== existingEvent.jobTypeId ||
      (currentJobStatus === 'Confirmed' && existingEvent.logJobStatus !== 'Confirmed')
    );
  }, [existingEvent, effectiveCapacityDate, currentJobTypeId, currentJobStatus]);

  // Ref to track previous values to prevent infinite loops
  const previousValuesRef = useRef<Record<string, any>>({});
  const jobClassificationRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Read the day's windows through a ref so the watch-subscription effect below
  // doesn't rebuild when they recompute (e.g. on a date change) — the
  // subscription then lives for the whole mount.
  const sortedWindowsRef = useRef(sortedWindows);
  useEffect(() => {
    sortedWindowsRef.current = sortedWindows;
  }, [sortedWindows]);

  // Watch form changes and update parent form state to maintain state across date changes
  useEffect(() => {
    if (!onFormDataChange) return;

    // Watch all form fields to maintain complete form state
    const subscription = form.watch((value, { name }) => {
      if (name === 'timeWindowId' && value.timeWindowId) {
        // Find the selected time window to get start/end times
        const selectedWindow = sortedWindowsRef.current.find((w) => w.id === value.timeWindowId);

        if (selectedWindow) {
          const updatedFormData: Partial<Event> = {
            timeStarted: normalizeTime(selectedWindow.startTime),
            timeEnded: normalizeTime(selectedWindow.endTime),
          };

          // Update parent form data with just the time information
          onFormDataChange(updatedFormData, { timeWindowId: true });
        }
      } else if (
        name === 'departmentId' ||
        name === 'logJobStatus' ||
        name === 'description' ||
        name === 'name' ||
        name === 'logNotes' ||
        name === 'technicianIds' ||
        name === 'jobTypeId' ||
        name === 'jobSubtypeId' ||
        name === 'logPaymentType' ||
        name === 'amountQuoted' ||
        name === 'isCallback' ||
        name === 'callbackOriginalId' ||
        name === 'highPriority' ||
        name === 'isLmpc' ||
        name === 'isResidential'
      ) {
        // Check if value actually changed to prevent infinite loops
        const currentValue = value[name];
        const previousValue = previousValuesRef.current[name];

        // For arrays (like technicianIds), do a deep comparison
        const hasChanged =
          name === 'technicianIds'
            ? JSON.stringify(currentValue) !== JSON.stringify(previousValue)
            : currentValue !== previousValue;

        if (!hasChanged) {
          return; // Skip update if value hasn't changed
        }

        // Update the ref with the new value
        previousValuesRef.current[name] = currentValue;

        const formToEventFieldMap: Record<string, string> = {
          isNewCustomer: 'isNewCustomer',
        };
        const eventFieldName = formToEventFieldMap[name] || name;

        const updatedFormData: Partial<Event> = {
          [eventFieldName]: currentValue,
        };

        if (name === 'technicianIds') {
          updatedFormData.logTechnicianAssigned = Array.isArray(currentValue)
            ? currentValue.join(',')
            : '';
        }

        onFormDataChange(updatedFormData, { [name]: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onFormDataChange]);

  // Ownership rule: while this step is mounted, the FORM owns every user-editable
  // field; the parent draft (formState) is only a write-behind mirror used to
  // restore the wizard after a remount. The one exception is `description` — the
  // "Services needed" banner in JobCreationFlow edits it outside this form — so
  // that's the only field synced from the draft back into the form. Re-asserting
  // the other draft fields here let a stale draft overwrite fresh user input
  // (e.g. Sector snapping back to Residential).
  useEffect(() => {
    if (!formState || existingEvent) return; // skip during edit/reschedule

    if (
      formState.description !== undefined &&
      form.getValues('description') !== formState.description
    ) {
      form.setValue('description', formState.description, { shouldDirty: false });
    }
  }, [formState, existingEvent, form]);

  // Keep timeWindowId in sync with selectedTimeWindow prop (skip when call window is hidden)
  // For new jobs: don't mark dirty (just initialization)
  // For existing events (reschedule): mark dirty when window differs from ORIGINAL event time
  useEffect(() => {
    if (hideCallWindowSelection) {
      form.setValue('timeWindowId', CUSTOM_TIME_WINDOW_ID, { shouldValidate: true });
      return;
    }
    const newId = selectedTimeWindow?.id ?? effectiveInitialWindowId;
    if (!newId) return;

    const currentId = form.getValues('timeWindowId');

    // For existing events, check if the selected window differs from the ORIGINAL event's time
    // This handles the case where the form initializes with a different window (e.g., original was past)
    let shouldMarkDirty = false;
    if (existingEvent && selectedTimeWindow) {
      const windowMatchesOriginal =
        normalizeTime(selectedTimeWindow.startTime) === normalizeTime(existingEvent.timeStarted) &&
        normalizeTime(selectedTimeWindow.endTime) === normalizeTime(existingEvent.timeEnded);
      shouldMarkDirty = !windowMatchesOriginal;
    }

    // Only update if the value is different OR we need to ensure dirty state is set
    if (currentId !== newId || (shouldMarkDirty && !form.formState.dirtyFields.timeWindowId)) {
      form.setValue('timeWindowId', newId, {
        shouldDirty: shouldMarkDirty,
        shouldValidate: true,
      });
    }
  }, [hideCallWindowSelection, selectedTimeWindow, effectiveInitialWindowId, existingEvent, form]);

  // Reset callback original ID when callback is unchecked
  useEffect(() => {
    if (currentIsCallback === 0) {
      form.setValue('callbackOriginalId', 0);
    }
  }, [currentIsCallback, form]);

  const { data: referralSources = [] } = useReferralSourceListQuery();
  const { data: contactPreferences = [] } = useContactPreferenceListQuery();
  const selectedReferralSourceId = form.watch('referralSourceId');
  const isReferralOther =
    referralSources.find((s) => String(s.id) === selectedReferralSourceId)?.label === 'Other';

  // Submitting with gaps leaves the form open and walks the user to the first one.
  const handleInvalid: SubmitErrorHandler<DetailsFormValues> = (errors) => {
    const formEl = formRef.current;
    if (!formEl) return;

    const errorNames = Object.keys(errors);
    // Wait a frame so the error styling is painted before focus lands on the field.
    requestAnimationFrame(() => anchorFirstInvalidField(formEl, errorNames));
  };

  const handleSubmit: SubmitHandler<DetailsFormValues> = (vals) => {
    // When call window is hidden (e.g. job info edit), always use custom time from formState
    const isUsingCustomTime =
      hideCallWindowSelection || vals.timeWindowId === CUSTOM_TIME_WINDOW_ID;

    // For custom time, we must have valid times in formState
    if (isUsingCustomTime) {
      if (!formState?.timeStarted || !formState?.timeEnded) {
        toast({
          title: 'Custom time required',
          description: 'Please set a custom start and end time.',
          variant: 'destructive',
        });
        return;
      }
    } else {
      // For preset windows, find and validate the window
      const win = sortedWindows.find((w) => w.id === vals.timeWindowId);

      if (!win) {
        return;
      }

      // Prevent scheduling into a fully blocked window (partial blocks still allow scheduling)
      if (windowBlocks && windowBlocks.length > 0) {
        const blocksForDay = getBlocksForDate(windowBlocks, date).filter(
          (b) => b.departmentId === vals.departmentId,
        );
        const blockedIds = getBlockedJobTypesForWindow(blocksForDay, win.startTime, win.endTime);
        const isFullBlock =
          blockedIds.length > 0 &&
          (blockedIds.some((id) => id === 0) ||
            (totalJobTypeCount > 0 && blockedIds.length >= totalJobTypeCount));
        if (isFullBlock) {
          toast({
            title: 'Time window is blocked',
            description:
              'The selected time window is currently blocked for this department. Please choose another or unblock it first.',
            variant: 'destructive',
          });
          return;
        }
      }

      // Hard block: maintenance jobs cannot be scheduled into a window that has hit its
      // maintenance limit. Effective limit is this department's override if set, else the
      // window's base. -1 = no limit, 0 = none.
      const { maintMax: effectiveMaintMax } = resolveWindowLimits(
        win,
        windowLimitOverrides,
        windowLimitDateOverrides,
        effectiveCapacityDate,
      );
      if (vals.jobTypeId === MAINTENANCE_JOB_TYPE_ID && effectiveMaintMax >= 0) {
        const confirmedMaint = windowMaintConfirmedCounts[win.id]?.[vals.departmentId] ?? 0;
        if (confirmedMaint >= effectiveMaintMax) {
          toast({
            title: 'Maintenance limit reached',
            description:
              'This time window has reached its maintenance limit for this department. Please choose another window.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    // Hard block: the day's cap for this job type is already met. Checked outside the
    // per-window branch above because the cap spans the whole day -- switching windows does
    // not help, so it applies to custom times and hidden-window flows too.
    //
    // Gated on capConsumingSave so an edit that doesn't add to the day's count is never blocked;
    // otherwise a Pending job on a full day, or anything on an over-cap day, becomes unsaveable.
    if (capConsumingSave && dailyCapState?.reached) {
      toast({
        title: `Daily ${dailyCapState.jobTypeName} cap reached`,
        description: `${dailyCapState.jobTypeName} is capped at ${dailyCapState.max} per day for this department, and ${format(parseISO(effectiveCapacityDate), 'EEEE, MMM d')} already has ${dailyCapState.used} confirmed. Raise the cap in Call Window Limits or pick a different day.`,
        variant: 'destructive',
      });
      return;
    }

    const techCsv = (vals.technicianIds ?? []).join(',');

    // Use custom times if available or when call window hidden, otherwise use window times
    const win = sortedWindows.find((w) => w.id === vals.timeWindowId);
    const startTime =
      isUsingCustomTime || hideCallWindowSelection
        ? normalizeTime(formState!.timeStarted!)
        : normalizeTime(win!.startTime);
    const endTime =
      isUsingCustomTime || hideCallWindowSelection
        ? normalizeTime(formState!.timeEnded!)
        : normalizeTime(win!.endTime);

    // Validate date range when editing with start/end dates (hideCallWindowSelection)
    if (hideCallWindowSelection && formState?.dateStarted && formState?.dateEnded) {
      const startDt = new Date(formState.dateStarted);
      const endDt = new Date(formState.dateEnded);
      if (endDt < startDt) {
        toast({
          title: 'Invalid date range',
          description: 'End date and time cannot be earlier than start date and time.',
          variant: 'destructive',
        });
        return;
      }
    }

    const eventPartial: Partial<Event> = {
      customer,
      departmentId: vals.departmentId,
      logJobStatus: vals.logJobStatus,
      dateStarted: hideCallWindowSelection && formState?.dateStarted ? formState.dateStarted : date,
      dateEnded: hideCallWindowSelection && formState?.dateEnded ? formState.dateEnded : undefined,
      timeStarted: startTime,
      timeEnded: endTime,
      name:
        vals.name ||
        (property?.address
          ? `${property.address}${property.city ? ' ' + property.city : ''}${property.state ? ', ' + property.state : ''} ${property.zip ?? ''}`.trim()
          : ''),
      description: vals.description || '',
      logNotes: vals.logNotes || '',
      logTechnicianAssigned: techCsv,
      jobTypeId: vals.jobTypeId,
      jobSubtypeId: vals.jobSubtypeId || 0,
      logPaymentType: vals.logPaymentType,
      amountQuoted: vals.amountQuoted || '',
      isCallback: vals.isCallback || 0,
      callbackOriginalId: vals.callbackOriginalId || 0,
      highPriority: vals.highPriority || 0,
      isLmpc: vals.isLmpc || 0,
      isResidential: vals.isResidential,
      isNewCustomer: vals.isNewCustomer || 0,
      serviceChannelWo: normalizeServiceChannelWo(vals.serviceChannelWo || ''),
      slaDue: parseSlaDue(vals.slaDue) ? vals.slaDue : '',
      customerPriorityId: vals.customerPriorityId || 0,
    };

    const customerData: CustomerLevelData = {
      customerId: customer.id,
      contactMethod: vals.contactMethod,
      enRouteCallNumber: vals.enRouteCallNumber,
      paymentAgreement: vals.paymentAgreement,
      referralSourceId: Number(vals.referralSourceId) || 0,
      contactPreferenceId: Number(vals.contactPreferenceId) || 0,
      referralSourceOther: isReferralOther ? vals.referralSourceOther ?? '' : '',
    };

    // If editing an existing event, show confirmation dialog
    if (existingEvent) {
      setPendingSubmitData({
        eventPartial,
        dirtyFields: form.formState.dirtyFields,
        customerData,
        internalNotes: vals.internalNotes,
      });
      setShowConfirmDialog(true);
    } else {
      // For new jobs, submit directly
      onSubmit(eventPartial, form.formState.dirtyFields, customerData, vals.internalNotes);
    }
  };

  // Handle confirmed submission
  const handleConfirmedSubmit = () => {
    if (pendingSubmitData) {
      onSubmit(
        pendingSubmitData.eventPartial,
        pendingSubmitData.dirtyFields,
        pendingSubmitData.customerData,
        pendingSubmitData.internalNotes,
      );
      setPendingSubmitData(null);
    }
    setShowConfirmDialog(false);
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setPendingSubmitData(null);
    setShowConfirmDialog(false);
  };

  // Blocked job type IDs for the currently selected window (for the selected dept + date)
  const blockedJobTypeIdsForWindow = useMemo(() => {
    if (!windowBlocks || !selectedTimeWindow) return new Set<number>();
    const blocksForDay = getBlocksForDate(windowBlocks, date).filter(
      (b) => b.departmentId === currentDepartmentId,
    );
    const ids = getBlockedJobTypesForWindow(
      blocksForDay,
      selectedTimeWindow.startTime,
      selectedTimeWindow.endTime,
    );
    // jobTypeId=0 means all types are blocked
    if (ids.some((id) => id === 0)) {
      return new Set(jobTypesData?.map((jt) => jt.id) ?? []);
    }
    return new Set(ids);
  }, [windowBlocks, selectedTimeWindow, date, currentDepartmentId, jobTypesData]);

  // Prepare options for dropdowns
  const jobTypeOptions = useMemo(() => {
    if (!jobTypesData) return [];
    return jobTypesData.map((type: JobType) => ({
      label: type.name || `Job Type ${type.id}`,
      value: type.id,
      blocked: blockedJobTypeIdsForWindow.has(type.id),
    }));
  }, [jobTypesData, blockedJobTypeIdsForWindow]);

  // Labels for the time-window header filter chip (current sector / department / job type)
  const bookingDepartmentLabel = departmentOptions.find(
    (d) => d.id === currentDepartmentId,
  )?.description;
  const bookingJobTypeLabel = jobTypeOptions.find((t) => t.value === currentJobTypeId)?.label;
  const bookingSectorLabel = (RESIDENTIAL_OPTIONS as { label: string; value: number }[]).find(
    (o) => o.value === currentIsResidential,
  )?.label;

  const jobSubtypeOptions = useMemo(() => {
    if (!currentJobTypeId || !jobSubtypesData?.results) return [];
    return buildSubtypeOptions({
      departmentId: currentDepartmentId,
      jobTypeId: currentJobTypeId,
      subtypes: jobSubtypesData.results,
      links: jobTypeSubtypes,
      placeholderLabel: 'Select subtype...',
      requiresSubtype,
      selectedId: existingEvent?.jobSubtypeId ?? 0,
    });
  }, [
    jobTypeSubtypes,
    currentJobTypeId,
    jobSubtypesData,
    currentDepartmentId,
    requiresSubtype,
    existingEvent?.jobSubtypeId,
  ]);
  const subtypeTradeGroups = useFeatureFlag('job-forms.subtype-trade-groups');
  const { data: subtypeTrades } = useSubtypeTradesQuery({
    departmentId: currentDepartmentId,
    jobTypeId: currentJobTypeId,
    isResidential: currentIsResidential,
    enabled: subtypeTradeGroups,
  });
  const jobSubtypeGroups = useMemo(
    () => groupSubtypeOptions(jobSubtypeOptions, subtypeTrades),
    [jobSubtypeOptions, subtypeTrades],
  );

  // Booking from a non-default trade's chip starts on that trade's catch-all Sub Type,
  // so an untouched Sub Type still files the job under the chip's trade.
  useEffect(() => {
    if (existingEvent || !bookingTrade?.multiTrade || !defaultTradeId) return;
    if (defaultTradeId === bookingTrade.deptTradeId) return;
    if (!(MEP_TRADE_IDS as readonly number[]).includes(defaultTradeId) || currentJobSubtypeId)
      return;
    const catchAll = TRADE_CATCH_ALL_SUBTYPE_ID[defaultTradeId];
    if (!catchAll || !jobSubtypeOptions.some((o) => o.value === catchAll)) return;
    form.setValue('jobSubtypeId', catchAll, { shouldDirty: false, shouldValidate: true });
  }, [existingEvent, bookingTrade, defaultTradeId, currentJobSubtypeId, jobSubtypeOptions, form]);

  const {
    options: callbackJobOptions,
    isLoading: callbackJobsLoading,
    linkWarning,
  } = useCallbackJobOptions({
    propertyId: property?.id || 0,
    excludeEventId: existingEvent?.id || 0,
    selectedId: form.watch('callbackOriginalId'),
  });

  // Property contacts: prefer the "show on jobs" contact as the phone-to-call option.
  const { data: propertyContactsData } = usePropertyContactsQuery({
    propertyId: property?.id || 0,
    enabled: !!property?.id,
  });

  const customerPhoneOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    const customerName = `${customer?.firstname ?? ''} ${customer?.lastname ?? ''}`.trim();

    const jobsContact = propertyContactsData?.results.find((c) => c.showOnJobs === 1);
    if (jobsContact) {
      const jcName =
        jobsContact.businessName ||
        [jobsContact.firstName, jobsContact.lastName].filter(Boolean).join(' ') ||
        'Property Contact';
      const jcTitle = jobsContact.title ? `, ${jobsContact.title}` : '';
      if (jobsContact.phone?.trim()) {
        options.push({
          label: `${jobsContact.phone} — ${jcName}${jcTitle} (Show on jobs)`,
          value: jobsContact.phone,
        });
      }
      if (jobsContact.altPhone?.trim()) {
        options.push({
          label: `${jobsContact.altPhone} — ${jcName}${jcTitle} (Alt)`,
          value: jobsContact.altPhone,
        });
      }
    }

    if (customer?.phone?.trim()) {
      options.push({
        label: `${customer.phone} — ${customerName} (Account Holder)`,
        value: customer.phone,
      });
    }
    if (customer?.cellphone?.trim()) {
      options.push({
        label: `${customer.cellphone} — ${customerName} (Cell)`,
        value: customer.cellphone,
      });
    }
    if (customer?.altphone?.trim()) {
      options.push({
        label: `${customer.altphone} — ${customerName} (Alt Phone)`,
        value: customer.altphone,
      });
    }
    if (customer?.fax?.trim()) {
      options.push({
        label: `${customer.fax} — ${customerName} (Fax)`,
        value: customer.fax,
      });
    }
    if (property?.phone?.trim()) {
      const propName = `${property.firstname ?? ''} ${property.lastname ?? ''}`.trim();
      options.push({
        label: `${property.phone} — ${propName || 'Property Contact'}`,
        value: property.phone,
      });
    }
    if (property?.altphone?.trim()) {
      const propName = `${property.firstname ?? ''} ${property.lastname ?? ''}`.trim();
      options.push({
        label: `${property.altphone} — ${propName || 'Property Alt'}`,
        value: property.altphone,
      });
    }
    // Collapse identical numbers to one row, keeping the richest (longest) label.
    const deduped = new Map<string, { label: string; value: string }>();
    for (const opt of options) {
      const key = normalizePhoneForSearch(opt.value);
      if (!key) continue;
      const existing = deduped.get(key);
      if (!existing || opt.label.length > existing.label.length) {
        deduped.set(key, opt);
      }
    }
    return Array.from(deduped.values());
  }, [customer, property, propertyContactsData]);

  // -------------------- Recent-visit callback prompt --------------------
  // Surface the most recent completed visit at this property within the last 30
  // days so the CSR can flag the new job as a callback (new jobs only).
  const compact = useSchedulerStyle();
  const recentCompletedVisit = useRecentCompletedVisit(
    propertyEventsQuery.data?.results,
    !existingEvent,
  );

  // Same warning as Step 2, repeated here so it stays in front of the CSR while
  // the call is actually being booked. Informational only -- it gates nothing.
  const serviceAreaCheck = useOutsideServiceArea(property, currentIsResidential);

  // Dismissal is owned by JobCreationFlow so a "No" in Step 2 persists here.
  // Show until the CSR answers, and hide once callback is already flagged.
  const showCallbackPrompt =
    !!recentCompletedVisit && !callbackPromptDismissed && currentIsCallback !== 1;

  // "Yes" — flag the job as a callback and preselect the recent visit as the
  // original job. We don't dismiss here: the prompt hides because isCallback is
  // now 1, and re-appears if the user unchecks the Callback flag.
  const handleMarkAsCallback = () => {
    if (!recentCompletedVisit) return;
    form.setValue('isCallback', 1, { shouldDirty: true, shouldValidate: true });
    form.setValue('callbackOriginalId', recentCompletedVisit.event.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // -------------------- JSX --------------------
  return (
    <FormContext {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="space-y-6"
      >
        {/* Scheduling banner moved to parent component */}
        {/* Customer / Property / Contract grid */}
        <CustomerPropertyContractGrid
          customer={customer}
          property={property}
          onChangeCustomer={onChangeCustomer}
          onChangeProperty={onChangeProperty}
          existingEvent={!!existingEvent}
          onShowCustomerJobs={() => setShowCustomerJobsDialog(true)}
        />

        {/* Outside service area (informational -- scheduling stays enabled) */}
        <OutsideServiceAreaBanner {...serviceAreaCheck} />

        {/* Job Details Section */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Job Details</h2>
          </div>

          {/* ── JOB CLASSIFICATION ── */}
          <div ref={jobClassificationRef} className="space-y-4">
            <SectionHeader>Job Classification</SectionHeader>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Sector */}
              <FormField
                control={form.control}
                name="isResidential"
                render={({ field }) => (
                  <FormItem data-anchor-field="isResidential">
                    <RequiredLabel>Sector</RequiredLabel>
                    <Select
                      value={field.value === UNSET_SECTOR ? '' : String(field.value)}
                      onValueChange={(v) => field.onChange(Number(v))}
                    >
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(RESIDENTIAL_OPTIONS as { label: string; value: number }[]).map((o) => (
                          <SelectItem key={o.value} value={String(o.value)}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Department */}
              <FormField
                control={form.control}
                name="departmentId"
                render={({ field }) => (
                  <FormItem data-anchor-field="departmentId">
                    <RequiredLabel>Department</RequiredLabel>
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => {
                        const departmentId = Number(v);
                        field.onChange(departmentId);
                        // Refrigeration is hidden under MEP
                        if (
                          departmentId === RESIDENTIAL_MEP_DEPARTMENT_ID &&
                          form.getValues('jobSubtypeId') === REFRIGERATION_SUBTYPE_ID
                        ) {
                          form.setValue('jobSubtypeId', 0, {
                            shouldDirty: true,
                            shouldValidate: form.formState.isSubmitted,
                          });
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {departmentOptions.map((d: TimesheetDepartment) => (
                          <SelectItem key={d.id} value={String(d.id)}>
                            {d.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Job Type */}
              <FormField
                control={form.control}
                name="jobTypeId"
                render={({ field }) => (
                  <FormItem data-anchor-field="jobTypeId">
                    <RequiredLabel>Job Type</RequiredLabel>
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => {
                        const jobTypeId = Number(v);
                        field.onChange(jobTypeId);
                        // Clear a Sub Type the new job type doesn't offer
                        const subtypeId = form.getValues('jobSubtypeId') ?? 0;
                        if (
                          subtypeId &&
                          !jobTypeSubtypes.some(
                            (l) => l.jobTypeId === jobTypeId && l.jobSubtypeId === subtypeId,
                          )
                        ) {
                          form.setValue('jobSubtypeId', 0, {
                            shouldDirty: true,
                            shouldValidate: form.formState.isSubmitted,
                          });
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select dept first" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jobTypeOptions.map((type) => (
                          <SelectItem
                            key={type.value}
                            value={String(type.value)}
                            disabled={type.blocked}
                          >
                            {type.blocked ? `${type.label} (blocked)` : type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Job Subtype */}
              <FormField
                control={form.control}
                name="jobSubtypeId"
                render={({ field }) => (
                  <FormItem>
                    {subtypeRequired ? (
                      <RequiredLabel>Job Subtype</RequiredLabel>
                    ) : (
                      <OptionalLabel>Job Subtype</OptionalLabel>
                    )}
                    <FormControl>
                      <Select
                        value={!field.value && subtypeRequired ? '' : String(field.value)}
                        onValueChange={(v) => field.onChange(Number(v))}
                        disabled={!currentJobTypeId}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              currentJobTypeId ? 'Select subtype...' : 'Select type first'
                            }
                          />
                        </SelectTrigger>
                        {subtypeTradeGroups ? (
                          <SelectContent
                            viewportClassName={SUBTYPE_SELECT_VIEWPORT_CLASS}
                            avoidCollisions={false}
                          >
                            <SubtypeSelectItems groups={jobSubtypeGroups} />
                          </SelectContent>
                        ) : (
                          <SelectContent>
                            {jobSubtypeOptions.map((subtype) => (
                              <SelectItem key={subtype.value} value={String(subtype.value)}>
                                {subtype.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        )}
                      </Select>
                    </FormControl>
                    <FormMessage />
                    <TradeChip name={tradeResolution?.name} />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 2: Status + Job Flags */}
            <div className="flex flex-wrap items-end gap-6">
              {/* Status */}
              <FormField
                control={form.control}
                name="logJobStatus"
                render={({ field }) => {
                  const displayStatus = field.value || 'Select status';
                  const hex = JOB_STATUS_COLORS[field.value] || JOB_STATUS_COLORS.Requested;
                  return (
                    <FormItem className="w-44" data-anchor-field="logJobStatus">
                      <RequiredLabel>Status</RequiredLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <span
                                className="h-3 w-3 shrink-0 rounded-sm border border-black/15"
                                style={{ backgroundColor: `#${hex}` }}
                                aria-hidden
                              />
                              <span className="truncate">{displayStatus}</span>
                            </div>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {jobStatusOptions.map((s: string) => (
                            <SelectItem key={s} value={s}>
                              <span className="flex items-center gap-2">
                                <span
                                  className="h-3 w-3 shrink-0 rounded-sm border border-black/15"
                                  style={{
                                    backgroundColor: `#${JOB_STATUS_COLORS[s] || JOB_STATUS_COLORS.Requested}`,
                                  }}
                                  aria-hidden
                                />
                                {s}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {/* Job Flags */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2">
                <span className="text-sm font-medium text-gray-700">
                  Job Flags{' '}
                  <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                </span>

                <FormField
                  control={form.control}
                  name="isCallback"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormControl>
                          <Checkbox
                            checked={field.value === 1}
                            onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm">Callback</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="highPriority"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormControl>
                          <Checkbox
                            checked={field.value === 1}
                            onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm">High Priority</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isLmpc"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormControl>
                          <Checkbox
                            checked={field.value === 1}
                            onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm">LMPC</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isNewCustomer"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1.5">
                        <FormControl>
                          <Checkbox
                            checked={field.value === 1}
                            onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm">New Customer</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Callback Original Job - only show when callback is checked */}
            {currentIsCallback === 1 && (
              <FormField
                control={form.control}
                name="callbackOriginalId"
                render={({ field }) => (
                  <FormItem data-anchor-field="callbackOriginalId">
                    <RequiredLabel>Callback Regarding Job</RequiredLabel>
                    <FormControl>
                      <Combobox
                        values={callbackJobOptions}
                        value={field.value && field.value !== 0 ? field.value.toString() : ''}
                        onChange={(value) => {
                          field.onChange(value ? Number(value) : 0);
                        }}
                        placeholder="Search by job #, type, or description..."
                        emptySearch="No completed jobs match"
                        disabled={!form.watch('isCallback') || callbackJobsLoading}
                        isLoading={callbackJobsLoading}
                        aria-required
                        triggerClassName={INVALID_FIELD_CLASSES}
                        contentClassName="w-[min(48rem,calc(100vw-2rem))]"
                      />
                    </FormControl>
                    {linkWarning && <p className="text-xs text-amber-700">{linkWarning}</p>}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Recent-visit callback prompt */}
            {compact && showCallbackPrompt && recentCompletedVisit && (
              <CallbackPrompt
                label={recentCompletedVisit.label}
                onYes={handleMarkAsCallback}
                onNo={() => onDismissCallbackPrompt?.()}
              />
            )}
          </div>

          {/* ── CALL DETAILS ── */}
          <div className="mt-6 space-y-4">
            <SectionHeader>Call Details</SectionHeader>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Contact Method */}
              <FormField
                control={form.control}
                name="contactMethod"
                render={({ field }) => (
                  <FormItem data-anchor-field="contactMethod">
                    <RequiredLabel>Contact Method</RequiredLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(CONTACT_METHOD_OPTIONS as string[]).map((method) => (
                          <SelectItem key={method} value={method}>
                            {method}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>How customer contacted us.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* En Route Call */}
              <FormField
                control={form.control}
                name="enRouteCallNumber"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>En Route Call</OptionalLabel>
                    <FormControl>
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select phone number" />
                        </SelectTrigger>
                        <SelectContent>
                          {customerPhoneOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>Number for tech to call en route.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Referral Source */}
              <FormField
                control={form.control}
                name="referralSourceId"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>Referral Source</OptionalLabel>
                    <FormControl>
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select referral source" />
                        </SelectTrigger>
                        <SelectContent>
                          {referralSources.map((s) => (
                            <SelectItem key={s.id} value={String(s.id)}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contact Preference */}
              <FormField
                control={form.control}
                name="contactPreferenceId"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>Contact Preference</OptionalLabel>
                    <FormControl>
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select contact preference" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactPreferences.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isReferralOther && (
                <FormField
                  control={form.control}
                  name="referralSourceOther"
                  render={({ field }) => (
                    <FormItem>
                      <OptionalLabel>Please specify</OptionalLabel>
                      <FormControl>
                        <Input {...field} placeholder="How did they hear about us?" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Services Needed */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem data-anchor-field="description">
                  <div className="flex items-center gap-1">
                    <RequiredLabel>Services Needed</RequiredLabel>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-muted-foreground cursor-help text-sm">ⓘ</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-sm">
                            Describe what the customer needs done. Carried over from the
                            &quot;Services needed&quot; note at the top of the form.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <FormControl>
                    <Textarea
                      {...field}
                      aria-required
                      placeholder="Describe the customer's issue (e.g. AC is not cooling, will not go below 80)."
                      className={`h-28 ${INVALID_FIELD_CLASSES}`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Brief Description + Job Note side by side */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>Brief Description</OptionalLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Brief summary of the job."
                        className="h-20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="logNotes"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>Job Note</OptionalLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Additional notes for this job."
                        className="h-20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* ── QUOTE & PAYMENT ── */}
          <div className="mt-6 space-y-4">
            <SectionHeader>Quote &amp; Payment</SectionHeader>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Payment Type */}
              <FormField
                control={form.control}
                name="logPaymentType"
                render={({ field }) => (
                  <FormItem data-anchor-field="logPaymentType">
                    <RequiredLabel>Payment Type</RequiredLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select payment type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentTypeOptionsFor(field.value).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Billing Terms */}
              <FormField
                control={form.control}
                name="paymentAgreement"
                render={({ field }) => (
                  <FormItem data-anchor-field="paymentAgreement">
                    <div className="flex items-center gap-1">
                      <RequiredLabel>Billing Terms</RequiredLabel>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground cursor-help text-sm leading-none">
                              ⓘ
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">
                              Customer billing terms. Updates the customer record.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger aria-required className={INVALID_FIELD_CLASSES}>
                          <SelectValue placeholder="Select agreement" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[
                          ...(BILLING_TERMS_OPTIONS as string[]),
                          ...(field.value === 'Card On File' ? ['Card On File'] : []),
                        ].map((term) => (
                          <SelectItem key={term} value={term}>
                            {term}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(field.value === 'COD' || isTenantOccupied) && showCardOnFile && (
                      <div className="pt-2">
                        <CardOnFileChip
                          variant="block"
                          userId={customer.id}
                          grpcBaseUrl={ENDPOINT}
                          isMobile={isMobileDevice}
                          warnWhenEmpty={isTenantOccupied}
                          overrideCardToken={property?.helcimPreferredCardToken}
                          onTapEmpty={() => setCardOnFileModalOpen(true)}
                          onTapPopulated={() => setCardOnFileModalOpen(true)}
                        />
                      </div>
                    )}
                    <FormMessage />
                    {(field.value === 'COD' || isTenantOccupied) && showCardOnFile && (
                      <CardOnFileModal
                        open={cardOnFileModalOpen}
                        onOpenChange={setCardOnFileModalOpen}
                        userId={customer.id}
                        grpcBaseUrl={ENDPOINT}
                        onTokenizeComplete={invalidateCardsOnFile}
                        onChangeCard={async (tokenId) => {
                          const client = new HelcimClient(ENDPOINT);
                          await client.setPreferredHelcimCard(
                            SetPreferredHelcimCardReq.create({
                              userId: customer.id,
                              cardToken: tokenId,
                            }),
                          );
                        }}
                      />
                    )}
                  </FormItem>
                )}
              />

              {/* Amount Quoted */}
              <FormField
                control={form.control}
                name="amountQuoted"
                render={({ field }) => (
                  <FormItem data-anchor-field="amountQuoted">
                    <div className="flex items-center gap-1">
                      <RequiredLabel>Amount Quoted</RequiredLabel>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground cursor-help text-sm leading-none">
                              ⓘ
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">
                              Write &quot;N/A&quot; if not relevant.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        aria-required
                        type="text"
                        placeholder="$0.00"
                        autoComplete="off"
                        className={INVALID_FIELD_CLASSES}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* ── DISPATCH & NOTES ── */}
          <div className="mt-6 space-y-4">
            <SectionHeader>Dispatch &amp; Notes</SectionHeader>

            <FormField
              control={form.control}
              name="assignTechnicians"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="rounded-[2px]"
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">Assign Technicians</FormLabel>
                    {!field.value && (
                      <span className="text-muted-foreground text-sm">Dispatch will assign.</span>
                    )}
                  </div>
                </FormItem>
              )}
            />

            {form.watch('assignTechnicians') && (
              <FormField
                control={form.control}
                name="technicianIds"
                render={({ field }) => (
                  <FormItem className="min-h-0">
                    <FormControl>
                      <div className="flex h-[300px] max-h-[300px] min-h-0 flex-col overflow-hidden">
                        <TechnicianAssignment
                          technicians={techniciansWithStatus}
                          selectedTechnicianIds={field.value || []}
                          onSelectionChange={(techIds) => field.onChange(techIds)}
                          departmentId={currentDepartmentId}
                          disabled={loading}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <div
              className={`grid grid-cols-1 gap-4 ${
                customerPriorities.length > 0 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
              }`}
            >
              <FormField
                control={form.control}
                name="serviceChannelWo"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>ServiceChannel WO #</OptionalLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="356458102"
                        onBlur={(e) => {
                          field.onChange(normalizeServiceChannelWo(e.target.value));
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {customerPriorities.length > 0 && (
                <FormField
                  control={form.control}
                  name="customerPriorityId"
                  render={({ field }) => {
                    const selected = customerPriorities.find((p) => p.id === field.value);
                    return (
                      <FormItem>
                        <FormLabel>
                          Priority{' '}
                          <span className="text-muted-foreground text-xs font-normal">
                            (from customer)
                          </span>
                        </FormLabel>
                        <Select
                          value={field.value ? String(field.value) : ''}
                          onValueChange={(v) => {
                            const priority = customerPriorities.find((p) => p.id === Number(v));
                            field.onChange(Number(v));
                            if (priority) {
                              form.setValue(
                                'slaDue',
                                format(
                                  addResponseTime(
                                    jobStartForSla(),
                                    priority.responseTime,
                                    priority.responseTimeUnitTypeId,
                                  ),
                                  'yyyy-MM-dd HH:mm:ss',
                                ),
                                { shouldDirty: true },
                              );
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {customerPriorities.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selected && (
                          <p className="text-muted-foreground text-xs">
                            {customerPriorityHint(selected)}
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}
              <FormField
                control={form.control}
                name="slaDue"
                render={({ field }) => (
                  <FormItem>
                    <OptionalLabel>SLA Due</OptionalLabel>
                    <FormControl>
                      <DateTimePicker
                        value={parseSlaDue(field.value)}
                        onChange={(d) =>
                          field.onChange(d && isValid(d) ? format(d, 'yyyy-MM-dd HH:mm:ss') : '')
                        }
                        granularity="minute"
                        hourCycle={12}
                        step={15}
                      />
                    </FormControl>
                    {form.watch('customerPriorityId') && field.value ? (
                      <p className="text-muted-foreground text-xs">
                        Pre-filled from priority — editable
                      </p>
                    ) : (
                      formatSlaDueHint(field.value) && (
                        <p className="text-muted-foreground text-xs">
                          {formatSlaDueHint(field.value)}
                        </p>
                      )
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Internal Notes */}
            <FormField
              control={form.control}
              name="internalNotes"
              render={({ field }) => (
                <FormItem>
                  <OptionalLabel>Internal Notes</OptionalLabel>
                  <div className="rounded-md border">
                    <div className="text-muted-foreground flex items-center justify-between border-b bg-gray-50 px-3 py-1.5 text-xs">
                      <span>
                        {new Date().toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}{' '}
                        {new Date().toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                      <span>
                        CSR: {auth.user.firstname} {auth.user.lastname?.charAt(0)}.
                      </span>
                    </div>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Call history, internal flags..."
                        className="border-0 shadow-none focus-visible:ring-0"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Time Window Availability Section (hidden when hideCallWindowSelection) */}
        {hideCallWindowSelection ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Start & End Date & Time</h3>
            {/* This flow renders no CallWindowAvailability, so without this the daily cap would
                only ever surface as a toast after Save. Shown while the date is being picked. */}
            {capConsumingSave && dailyCapState?.reached && (
              <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3">
                <p className="text-xs text-red-700">
                  <span className="font-semibold">
                    Daily {dailyCapState.jobTypeName} cap reached
                  </span>{' '}
                  — {dailyCapState.jobTypeName} is capped at {dailyCapState.max} per day for this
                  department, and {format(parseISO(effectiveCapacityDate), 'EEEE, MMM d')} already
                  has {dailyCapState.used} confirmed. Saving this date will be rejected.
                </p>
              </div>
            )}
            {/* Start date, end date, start time, end time from formState (dateStarted/dateEnded full datetime or date + timeStarted/timeEnded) */}
            {(() => {
              const startDateStr = formState?.dateStarted?.split(' ')[0] ?? date;
              const endDateStr = formState?.dateEnded?.split(' ')[0] ?? startDateStr;
              const startTimeStr =
                formState?.timeStarted ??
                (formState?.dateStarted?.includes(' ')
                  ? formState.dateStarted.split(' ')[1]?.slice(0, 5)
                  : undefined) ??
                '00:00';
              const endTimeStr =
                formState?.timeEnded ??
                (formState?.dateEnded?.includes(' ')
                  ? formState.dateEnded.split(' ')[1]?.slice(0, 5)
                  : undefined) ??
                '12:00';
              const setStartDate = (newDate: string) => {
                const time = formState?.timeStarted ?? startTimeStr;
                const start = {
                  dateStarted: `${newDate} ${normalizeTime(time)}`,
                  timeStarted: normalizeTime(time),
                };
                if (newDate > endDateStr) {
                  // A start past the end pulls the end to the same day.
                  const endTime = normalizeTime(formState?.timeEnded ?? endTimeStr);
                  onFormDataChange?.(
                    { ...start, dateEnded: `${newDate} ${endTime}`, timeEnded: endTime },
                    { timeWindowId: true, dateStarted: true, dateEnded: true },
                  );
                  return;
                }
                onFormDataChange?.(start, { timeWindowId: true, dateStarted: true });
              };
              const setEndDate = (newDate: string) => {
                if (newDate < startDateStr) {
                  toast({
                    title: 'Invalid end date',
                    description: 'End date cannot be earlier than start date.',
                    variant: 'destructive',
                  });
                  return;
                }
                const time = formState?.timeEnded ?? endTimeStr;
                onFormDataChange?.(
                  {
                    dateEnded: `${newDate} ${normalizeTime(time)}`,
                    timeEnded: normalizeTime(time),
                  },
                  { timeWindowId: true, dateEnded: true },
                );
              };
              const setStartTime = (value: string) => {
                onFormDataChange?.(
                  {
                    dateStarted: `${startDateStr} ${normalizeTime(value)}`,
                    timeStarted: normalizeTime(value),
                    dateEnded: formState?.dateEnded,
                    timeEnded: formState?.timeEnded ?? endTimeStr,
                  },
                  { timeWindowId: true, dateStarted: true },
                );
              };
              const setEndTime = (value: string) => {
                onFormDataChange?.(
                  {
                    dateStarted: formState?.dateStarted,
                    timeStarted: formState?.timeStarted ?? startTimeStr,
                    dateEnded: `${endDateStr} ${normalizeTime(value)}`,
                    timeEnded: normalizeTime(value),
                  },
                  { timeWindowId: true, dateEnded: true },
                );
              };
              return (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-date">Start date</Label>
                    <DatePicker
                      value={parseISO(startDateStr)}
                      onChange={(d) => d && setStartDate(format(d, 'yyyy-MM-dd'))}
                      placeholder="Select start date"
                      displayFormat={{ hour24: 'EEEE, MMM d, yyyy', hour12: 'EEEE, MMM d, yyyy' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-end-date">End date</Label>
                    <DatePicker
                      value={parseISO(endDateStr)}
                      onChange={(d) => d && setEndDate(format(d, 'yyyy-MM-dd'))}
                      placeholder="Select end date"
                      displayFormat={{ hour24: 'EEEE, MMM d, yyyy', hour12: 'EEEE, MMM d, yyyy' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-time">Start time</Label>
                    <TimePicker
                      date={parseISO(`${startDateStr}T${startTimeStr.slice(0, 5)}:00`)}
                      onChange={(d) => d && setStartTime(format(d, 'HH:mm'))}
                      hourCycle={12}
                      granularity="minute"
                      step={15}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-end-time">End time</Label>
                    <TimePicker
                      date={parseISO(`${endDateStr}T${endTimeStr.slice(0, 5)}:00`)}
                      onChange={(d) => d && setEndTime(format(d, 'HH:mm'))}
                      hourCycle={12}
                      granularity="minute"
                      step={15}
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          // Anchoring aligns this card to the top of the scrollport, which the compact
          // header is stuck over, so hold it clear of the header.
          <div
            className={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm ${compact ? 'scroll-mt-52' : ''}`}
            data-anchor-field="timeWindowId"
            data-anchor-block="start"
            tabIndex={-1}
            aria-label="Select Time Window, required"
          >
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Select Time Window</h3>

            {/* Lives with the picker so the error reads next to the control that sets it. */}
            <FormField
              control={form.control}
              name="timeWindowId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <input type="hidden" {...field} />
                  </FormControl>
                  <FormMessage className="mb-4" />
                </FormItem>
              )}
            />
            {currentDepartmentId ? (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <span className="text-sm text-gray-700">
                  Booking{' '}
                  <span className="font-medium">
                    {bookingSectorLabel ? `${bookingSectorLabel} ` : ''}
                    {bookingDepartmentLabel ?? 'Department'}
                  </span>
                  {bookingJobTypeLabel ? (
                    <>
                      {' · '}
                      <span className="font-medium">{bookingJobTypeLabel}</span>
                    </>
                  ) : null}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    jobClassificationRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }
                >
                  Change
                </Button>
              </div>
            ) : null}
            <CallWindowAvailability
              selectedDate={date}
              callWindows={callWindows}
              selectedWindow={selectedTimeWindow ?? null}
              departmentId={currentDepartmentId}
              windowEventCounts={windowEventCounts}
              windowTechCounts={windowTechCounts}
              windowJobTypeBreakdown={windowJobTypeBreakdown}
              windowMaintConfirmedCounts={windowMaintConfirmedCounts}
              windowLimitOverrides={windowLimitOverrides}
              windowLimitDateOverrides={windowLimitDateOverrides}
              dailyCapState={capConsumingSave ? dailyCapState : null}
              bookingJobTypeId={currentJobTypeId}
              dateBreakout={dateBreakout}
              isCustomTime={isCustomTime}
              onSelectWindow={(window) => {
                // For reschedule, mark dirty if the selected window differs from ORIGINAL event time
                const shouldMarkDirty = existingEvent
                  ? normalizeTime(window.startTime) !== normalizeTime(existingEvent.timeStarted) ||
                    normalizeTime(window.endTime) !== normalizeTime(existingEvent.timeEnded)
                  : false;

                // Update form with window ID, marking dirty if needed for reschedule
                form.setValue('timeWindowId', window.id, {
                  shouldValidate: true,
                  shouldDirty: shouldMarkDirty,
                });

                // Explicitly update parent formState with the new times
                // This is necessary because form.watch doesn't always trigger for programmatic setValue calls
                const newTimeStarted = normalizeTime(window.startTime);
                const newTimeEnded = normalizeTime(window.endTime);
                onFormDataChange?.(
                  { timeStarted: newTimeStarted, timeEnded: newTimeEnded },
                  shouldMarkDirty ? { timeWindowId: true } : undefined,
                );

                // Notify parent to update window state for proper highlighting
                onTimeWindowChange?.(window.id);
              }}
              onChangeDate={(newDate) => {
                onDateChange?.(newDate);
              }}
              onCustomTime={(startTime, endTime) => {
                // Apply custom times to form data
                const updatedFormData: Partial<Event> = {
                  timeStarted: normalizeTime(startTime), // HH:mm format
                  timeEnded: normalizeTime(endTime), // HH:mm format
                };

                // Mark time fields as dirty so they get saved when editing
                const dirtyFields: DirtyModelFields<DetailsFormValues> = {
                  timeWindowId: true, // Mark as dirty to trigger time update
                };

                // Set timeWindowId to -1 to indicate custom time (bypass window validation)
                form.setValue('timeWindowId', CUSTOM_TIME_WINDOW_ID, { shouldValidate: true });

                // Update parent form state with dirty tracking
                onFormDataChange?.(updatedFormData, dirtyFields);
              }}
              currentStartTime={formState.timeStarted}
              currentEndTime={formState.timeEnded}
              existingEvent={existingEvent}
              isCapacityLoading={isCapacityLoading}
              windowBlocks={windowBlocks}
            />
          </div>
        )}

        {/* Action buttons */}
        <div
          className={
            compact
              ? 'sticky bottom-0 -mx-6 flex justify-between gap-2 border-t border-gray-200 bg-white px-6 pb-6 pt-4'
              : 'flex justify-between gap-2 pt-4'
          }
        >
          <div className="flex gap-2">
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            {onCancel && !hideCancelButton && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={
              loading ||
              (!!existingEvent &&
                Object.keys({
                  ...(preservedDirtyFields || {}),
                  ...(form.formState.dirtyFields || {}),
                }).length === 0)
            }
            isLoading={loading}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {hideCallWindowSelection && existingEvent
              ? 'Update'
              : existingEvent
                ? 'Reschedule'
                : 'Schedule Job'}
          </Button>
        </div>
      </form>

      {/* Customer Jobs Dialog */}
      <Dialog open={showCustomerJobsDialog} onOpenChange={setShowCustomerJobsDialog}>
        <DialogContent className="theme-light max-h-[80vh] max-w-6xl overflow-hidden bg-white">
          <DialogHeader>
            <DialogTitle>
              Customer Jobs - {`${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim()}
              {property && property.address && (
                <div className="mt-1 text-sm font-normal text-gray-600">
                  {property.address}
                  {property.city && `, ${property.city}`} {property.state} {property.zip}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <CustomerJobsAccordion customer={customer} propertyId={property?.id} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule / Update Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="theme-light bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hideCallWindowSelection ? 'Confirm Update' : 'Confirm Reschedule'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {existingEvent && pendingSubmitData && (
                <>
                  You are about to {hideCallWindowSelection ? 'update' : 'reschedule'} this job.
                  <br />
                  <br />
                  {(() => {
                    const partial = pendingSubmitData.eventPartial;
                    const hasStartEndDates =
                      partial.dateStarted != null && partial.dateEnded != null;
                    const originalStartDate = existingEvent.dateStarted?.split(' ')[0] ?? '';
                    const originalEndDate =
                      existingEvent.dateEnded?.split(' ')[0] ?? originalStartDate;
                    const newStartDate =
                      hasStartEndDates && partial.dateStarted
                        ? partial.dateStarted.split(' ')[0]
                        : date;
                    const newEndDate =
                      hasStartEndDates && partial.dateEnded
                        ? partial.dateEnded.split(' ')[0]
                        : date;

                    const originalTime = `${formatTimeFull(existingEvent.timeStarted)} - ${formatTimeFull(existingEvent.timeEnded)}`;
                    const newTime = `${formatTimeFull(partial.timeStarted || '')} - ${formatTimeFull(partial.timeEnded || '')}`;

                    const startDateChanged = originalStartDate !== newStartDate;
                    const endDateChanged = originalEndDate !== newEndDate;
                    const timeChanged = originalTime !== newTime;

                    if (startDateChanged || endDateChanged || timeChanged) {
                      return (
                        <>
                          <strong>Changes:</strong>
                          <ul className="mt-2 list-inside list-disc">
                            {hasStartEndDates ? (
                              <>
                                <li>
                                  Start:{' '}
                                  <strong>
                                    {formatDateOptionalTime(
                                      originalStartDate,
                                      existingEvent.timeStarted,
                                    )}
                                  </strong>{' '}
                                  →{' '}
                                  <strong>
                                    {formatDateOptionalTime(
                                      newStartDate,
                                      partial.timeStarted || undefined,
                                    )}
                                  </strong>
                                </li>
                                <li>
                                  End:{' '}
                                  <strong>
                                    {formatDateOptionalTime(
                                      originalEndDate,
                                      existingEvent.timeEnded,
                                    )}
                                  </strong>{' '}
                                  →{' '}
                                  <strong>
                                    {formatDateOptionalTime(
                                      newEndDate,
                                      partial.timeEnded || undefined,
                                    )}
                                  </strong>
                                </li>
                              </>
                            ) : (
                              <>
                                {(startDateChanged || endDateChanged) && (
                                  <li>
                                    Date:{' '}
                                    <strong>
                                      {format(parseISO(originalStartDate), 'EEEE, MMMM d, yyyy')}
                                    </strong>{' '}
                                    →{' '}
                                    <strong>
                                      {format(parseISO(newStartDate), 'EEEE, MMMM d, yyyy')}
                                    </strong>
                                  </li>
                                )}
                                {timeChanged && (
                                  <li>
                                    Time: <strong>{originalTime}</strong> →{' '}
                                    <strong>{newTime}</strong>
                                  </li>
                                )}
                              </>
                            )}
                          </ul>
                        </>
                      );
                    }
                    return 'The job details will be updated.';
                  })()}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedSubmit}>
              {hideCallWindowSelection ? 'Confirm Update' : 'Confirm Reschedule'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormContext>
  );
};
